import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  editable,
  forDeveloper,
  UNRESOLVED,
} from '@platform/schema/generation';
import { QA_CATEGORY_LABEL } from '@platform/schema/qa-score';

import { ChangeSummary, fieldCount } from './ChangeSummary';
import { Commentary } from './Commentary';
import { fixFor, LessonFixFlow, STATUS_LABEL } from './LessonFix';
import { OUTCOME_LABEL } from './reviews';

import type { JobNote } from './regeneration';
import type { LessonFix } from '@platform/schema/lesson-fix';
import type { LessonWorkspace } from '@platform/schema/review';

/**
 * What the reviewer found, and what the editor did about it — SKILL-03, GEN-76.
 *
 * Findings have been written since GEN-05 and read by nothing: the only trace of one was
 * the blocker count on the publish panel, which says how many without saying what.
 *
 * The decision is the point of the panel rather than a corner of it. §8.3 keeps the
 * reviewer advisory, so an edit made on its advice cannot be more final than the advice
 * was: Fix with AI comes back as a proposal, and the scene changes only on Apply —
 * CURR-33. Everything a click can produce is said here, beside the finding it was asked
 * about — the progress line, the editor's reason for changing nothing, and the answers.
 */

type Finding = LessonWorkspace['findings'][number];
type Scene = LessonWorkspace['scenes'][number];

export const SEVERITY_COLOUR = {
  BLOCKER: 'error',
  WARN: 'warning',
  NOTE: 'default',
} as const;

const sceneOf = (
  finding: Finding,
  scenes: readonly Scene[],
): Scene | undefined =>
  finding.targetType === 'SCENE'
    ? scenes.find((scene) => scene.id === finding.targetId)
    : undefined;

/**
 * Which scene a finding names, in the number on the screen rather than an id.
 *
 * Read from `targetType`, not from whether a lookup misses. A `SCENE` finding whose scene
 * has since left the lesson is exactly the case a miss would silently relabel "This
 * lesson" — and a panel that exists to stop an opinion going unseen must not show one
 * pointed at the wrong thing.
 */
const where = (finding: Finding, scenes: readonly Scene[]): string => {
  if (finding.targetType !== 'SCENE') return 'This lesson';

  const scene = sceneOf(finding, scenes);

  return scene === undefined
    ? 'A scene no longer in this lesson'
    : `Scene ${String(scene.position)}`;
};

const blocking = (finding: Finding): boolean =>
  finding.category === undefined || finding.severity === 'BLOCKER';

/**
 * Why a finding matters, in what it does to a publish — CURR-38. A rule's finding has no
 * category; the publish rules refuse the lesson until it is gone. A reviewer's blocker
 * refuses a publish until it is resolved or acknowledged (§8.3).
 */
const whyItMatters = (finding: Finding): string => {
  if (finding.category === undefined)
    return 'Blocking: the publish rules refuse this lesson until it is fixed.';
  const score = `It lowers the ${QA_CATEGORY_LABEL[finding.category]} score.`;
  return blocking(finding)
    ? `Blocking: ${score} A publish is refused until it is resolved or you acknowledge it.`
    : `Advisory: ${score} It does not stop a publish.`;
};

/** Applied through the scene editor, and the re-check still finds it — CURR-36. */
const stillFound = (finding: Finding): boolean =>
  finding.status === 'OPEN' &&
  finding.changes !== undefined &&
  finding.revertedAt === undefined;

/** Where a finding stands: the newest lesson fix asked for it, else its own status. */
function statusOf(finding: Finding, fix: LessonFix | undefined): string {
  if (fix !== undefined && fix.status !== 'DISCARDED')
    return STATUS_LABEL[fix.status];
  if (stillFound(finding)) return STATUS_LABEL.UNRESOLVED;
  if (finding.status === 'OPEN' && finding.revertedAt !== undefined)
    return STATUS_LABEL.REVERTED;
  return {
    OPEN: STATUS_LABEL.DISCARDED,
    PROPOSED: STATUS_LABEL.PROPOSED,
    ACCEPTED: STATUS_LABEL.RESOLVED,
    DISMISSED: 'Allowed',
    SUPERSEDED: 'Replaced by a rebuild',
  }[finding.status];
}

/** When the editor acted, for an edit it made while nobody was watching. */
const on = (when: string | undefined): string =>
  when === undefined ? '' : ` on ${new Date(when).toLocaleString()}`;

export function Findings({
  title,
  lessonId,
  findings,
  fixes,
  scenes,
  busy,
  applying,
  note,
  onApply,
  onKeep,
  onDiscard,
  onRevert,
  onAllow,
  onShow,
  showRefused,
}: {
  /** The group's heading; the reviewer's count when there is only one group. */
  title?: string;
  lessonId: string;
  findings: readonly Finding[];
  /** The lesson's fixes, newest first — CURR-36. */
  fixes: readonly LessonFix[];
  scenes: readonly Scene[];
  /** A model turn is already in flight on this lesson, whoever asked for it. */
  busy: boolean;
  /** The finding the editor is working on, or was last asked about. */
  applying: string | undefined;
  /** Where that ask stands, rendered against that finding and nowhere else. */
  note: JobNote | undefined;
  onApply: (findingId: string) => void;
  onKeep: (findingId: string) => void;
  onDiscard: (findingId: string) => void;
  onRevert: (findingId: string) => void;
  /** Close an open finding without a fix — ADMIN-22. */
  onAllow: (findingId: string) => void;
  /** Play the preview at this scene, as the arc's Go to scene does. */
  onShow: (sceneId: string) => void;
  /** Why the preview cannot open on this scene, or nothing. */
  showRefused: (scene: Scene) => string | undefined;
}) {
  if (findings.length === 0) return null;

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">
        {title ??
          `The reviewer raised ${String(findings.length)} finding${findings.length === 1 ? '' : 's'}`}
      </Typography>

      {findings.map((finding) => {
        const fix = fixFor(fixes, finding.id);
        const scene = sceneOf(finding, scenes);
        const refused = scene === undefined ? undefined : showRefused(scene);
        return (
          <Paper
            key={finding.id}
            variant="outlined"
            sx={{ p: 1 }}
            data-testid="finding"
            // What the scorecard's breakdown links to: only an open finding moves a score.
            data-qa-category={
              UNRESOLVED.includes(finding.status) ? finding.category : undefined
            }
          >
            <Stack spacing={1}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', flexWrap: 'wrap' }}
              >
                <Chip
                  size="small"
                  label={finding.severity}
                  color={SEVERITY_COLOUR[finding.severity]}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={blocking(finding) ? 'Blocking' : 'Advisory'}
                />
                {/* Which score this one moved — GEN-89. Absent on a rule's refusal, and
                  the scorecard says how many of those there were. */}
                {finding.category !== undefined && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={QA_CATEGORY_LABEL[finding.category]}
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  {where(finding, scenes)}
                </Typography>
                <Chip size="small" label={statusOf(finding, fix)} />
                {finding.outcome !== undefined && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={finding.outcome === 'FIXED' ? 'success' : 'default'}
                    label={`Outcome: ${OUTCOME_LABEL[finding.outcome]}`}
                  />
                )}
              </Stack>

              {/* Both paragraphs are opinion about the lesson, not the lesson. */}
              <Commentary author="Reviewer">
                <Typography variant="body2">{finding.finding}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Why it matters: {whyItMatters(finding)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Proposed change: {finding.suggestion}
                </Typography>
              </Commentary>

              {/* Against the finding that was asked about, not at the top of the screen.
                A reviewer reading a warning should not have to look elsewhere to find
                out whether the button they just pressed did anything. */}
              {applying === finding.id && note !== undefined && (
                <Alert severity={note.severity}>{note.message}</Alert>
              )}

              {/* A failed re-check writes its note too, and it is not "changed nothing". */}
              {finding.editorNote !== undefined && (
                <Alert severity="warning">
                  {stillFound(finding)
                    ? finding.editorNote
                    : `The editor changed nothing. ${finding.editorNote}`}
                </Alert>
              )}

              {(scene !== undefined || finding.status === 'OPEN') && (
                <Stack direction="row" spacing={1}>
                  {scene !== undefined && (
                    // Wrapped so a disabled button still shows why — `SceneEditor` does it too.
                    <Tooltip title={refused ?? ''}>
                      <span>
                        <Button
                          size="small"
                          disabled={refused !== undefined}
                          onClick={() => {
                            onShow(scene.id);
                          }}
                        >
                          Show
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {finding.status === 'OPEN' && (
                    <Button
                      size="small"
                      disabled={busy}
                      onClick={() => {
                        onAllow(finding.id);
                      }}
                    >
                      Allow
                    </Button>
                  )}
                </Stack>
              )}

              {/* The scene editor answers what one scene rewrite can; the lesson fix the
                rest — CURR-36. A finding with a scene rewrite keeps that history. A
                developer finding is an app change, so no rewrite is offered — CURR-55.
                An allowed one is decided, and undoing that is not offered — ADMIN-22. */}
              {finding.status !== 'DISMISSED' &&
                (editable(finding) || finding.changes !== undefined ? (
                  <Acted
                    finding={finding}
                    busy={busy}
                    onApply={onApply}
                    onKeep={onKeep}
                    onDiscard={onDiscard}
                    onRevert={onRevert}
                  />
                ) : (finding.status === 'OPEN' && !forDeveloper(finding)) ||
                  fix !== undefined ? (
                  <LessonFixFlow
                    lessonId={lessonId}
                    fix={fix}
                    request={{ findingId: finding.id }}
                  />
                ) : null)}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

/**
 * What can be done about one finding, given what has already been done to it — CURR-33.
 *
 * A proposal is a diff and nothing on the scene, so Apply is the write and Discard leaves
 * no trace. An applied fix keeps its diff with Revert, which is the finding's history. A
 * reverted one keeps the diff *and* gets Fix with AI back: it is `OPEN` again, and taking
 * the comparison away at the click would remove it when it was just asked for.
 */
function Acted({
  finding,
  busy,
  onApply,
  onKeep,
  onDiscard,
  onRevert,
}: {
  finding: Finding;
  busy: boolean;
  onApply: (findingId: string) => void;
  onKeep: (findingId: string) => void;
  onDiscard: (findingId: string) => void;
  onRevert: (findingId: string) => void;
}) {
  const ask = editable(finding) ? (
    <Stack direction="row">
      <Button
        size="small"
        variant="outlined"
        disabled={busy}
        onClick={() => {
          onApply(finding.id);
        }}
      >
        Fix with AI
      </Button>
    </Stack>
  ) : null;

  if (finding.changes === undefined) return ask;

  const when = on(finding.appliedAt);

  if (finding.status === 'PROPOSED')
    return (
      <ChangeSummary
        changes={finding.changes}
        severity="warning"
        unchanged={`Fix with AI came back${when} with the same text. Nothing needs deciding.`}
        lead={`Fix with AI proposes changing ${fieldCount(finding.changes)}. Nothing changes until you apply it.`}
        actions={[
          {
            label: 'Apply',
            colour: 'primary',
            onClick: () => {
              onKeep(finding.id);
            },
          },
          {
            label: 'Discard',
            colour: 'error',
            onClick: () => {
              onDiscard(finding.id);
            },
          },
        ]}
        disabled={busy}
      />
    );

  // Applied is ACCEPTED, or OPEN with no `revertedAt` when its re-check failed — CURR-36.
  if (finding.status !== 'ACCEPTED' && !stillFound(finding))
    return (
      <Stack spacing={1}>
        <ChangeSummary
          changes={finding.changes}
          severity="info"
          unchanged="You reverted this fix."
          lead="You reverted this fix, so this finding is open again. This is what it had changed."
        />
        {ask}
      </Stack>
    );

  return (
    <ChangeSummary
      changes={finding.changes}
      severity={finding.status === 'ACCEPTED' ? 'success' : 'warning'}
      unchanged={`You applied this fix${when}. It came back with the same text.`}
      lead={
        finding.status === 'ACCEPTED'
          ? `You applied this fix, which changed ${fieldCount(finding.changes)}. Re-checked: the finding is resolved. The lesson needs verifying again.`
          : `You applied this fix, which changed ${fieldCount(finding.changes)}. The re-check still finds it, so the finding stays open.`
      }
      actions={[
        {
          label: 'Revert',
          colour: 'error',
          onClick: () => {
            onRevert(finding.id);
          },
        },
      ]}
      disabled={busy}
    />
  );
}
